import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { AccountService } from '../../services/account.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { BankRuleService } from '../../services/bankRule.service';
import { SegmentService } from '../../services/segment.service';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import {
  ACTION_FIELDS, ACTION_HELP, ACTION_LABEL,
  BankRule, BankRuleCondition, BankRuleRequest, BankRuleTestResult, MATCH_MODE_LABEL, OPERATOR_LABEL,
  RuleField, RuleKindInfo, RuleMatchMode, RuleOperator,
} from '../../interfaces/bank/bankRule.interface';
import { CustomerService } from '../../services/customer.service';
import { SupplierService } from '../../services/supplier.service';
import SalesCatalogueSelect from '../sales/SalesCatalogueSelect';
import TaxCodeSelect from '../tax/TaxCodeSelect';
import { useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import DepartmentPicker, { useDepartmentOptions } from '../costcentre/DepartmentPicker';
import { formatDate } from '../../utils/dateUtils';
import { leafAccounts } from '../../utils/postingAccounts';

const formatAmount = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface ConditionRow {
  field: string;
  operator: RuleOperator | '';
  valueText: string;
  valueNumber: string;
  valueNumberTo: string;
}

const emptyCondition = (field = ''): ConditionRow => ({ field, operator: '', valueText: '', valueNumber: '', valueNumberTo: '' });

const toRow = (c: BankRuleCondition): ConditionRow => ({
  field: c.field,
  operator: c.operator,
  valueText: c.valueText ?? '',
  valueNumber: c.valueNumber != null ? String(c.valueNumber) : '',
  valueNumberTo: c.valueNumberTo != null ? String(c.valueNumberTo) : '',
});

interface Props {
  /** The rule being edited; omit to create. */
  rule?: BankRule | null;
  /** Pre-filled draft for "Create rule from this line" (a new rule). */
  initial?: Partial<BankRuleRequest> | null;
  /** The institution the rule belongs to; defaults to the selected one. */
  institutionId?: string;
  onClose: () => void;
  onSaved: (rule: BankRule) => void;
}

/** Create / edit one bank rule: name, scope, match mode, condition rows, the post-to-account action, and a
 *  "Test against recent lines" panel that lists what the draft would match (read-only, nothing is posted). */
const BankRuleEditorDialog: React.FC<Props> = ({ rule, initial, institutionId: institutionIdProp, onClose, onSaved }) => {
  const toast = useToast();
  const institutionId = institutionIdProp ?? AuthService.getSelectedInstitution()!;
  const departmentOptions = useDepartmentOptions(institutionId);
  const [kind, setKind] = useState<RuleKindInfo | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [banks, setBanks] = useState<BankAccount[]>([]);

  const seed = rule ?? null;
  const [name, setName] = useState(seed?.name ?? initial?.name ?? '');
  const [bankAccountId, setBankAccountId] = useState(seed?.scopeId ?? initial?.scopeId ?? '');
  const [matchMode, setMatchMode] = useState<RuleMatchMode>(seed?.matchMode ?? initial?.matchMode ?? 'ALL');
  const [conditions, setConditions] = useState<ConditionRow[]>(() =>
    seed ? seed.conditions.map(toRow)
      : initial?.conditions?.length ? initial.conditions.map(toRow)
      : [emptyCondition('DESCRIPTION')]);
  const [accountId, setAccountId] = useState(seed?.action?.accountId ?? initial?.action?.accountId ?? '');
  const [segmentId, setSegmentId] = useState(seed?.action?.segmentId ?? initial?.action?.segmentId ?? '');
  const [departmentId, setDepartmentId] = useState(seed?.action?.departmentId ?? initial?.action?.departmentId ?? '');
  // ---- FW-4B: party / tax action fields ----
  const [actionType, setActionType] = useState<string>(seed?.action?.actionType ?? initial?.action?.actionType ?? 'POST_TO_ACCOUNT');
  const [customerId, setCustomerId] = useState(seed?.action?.customerId ?? initial?.action?.customerId ?? '');
  const [supplierId, setSupplierId] = useState(seed?.action?.supplierId ?? initial?.action?.supplierId ?? '');
  const [taxCodeId, setTaxCodeId] = useState(seed?.action?.taxCodeId ?? initial?.action?.taxCodeId ?? '');
  const [itemId, setItemId] = useState(seed?.action?.itemId ?? initial?.action?.itemId ?? '');
  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const taxLookup = useTaxCodeLookup(institutionId);
  const af = ACTION_FIELDS[actionType] ?? ACTION_FIELDS.POST_TO_ACCOUNT;
  // ---- end FW-4B ----
  const [template, setTemplate] = useState(seed?.action?.descriptionTemplate ?? initial?.action?.descriptionTemplate ?? '');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<BankRuleTestResult | null>(null);

  useEffect(() => {
    BankRuleService.fields().then(setKind).catch((e: any) => toast.showApiError(e, 'Failed to load rule fields'));
    AccountService.getAllAccounts()
      .then((data: Account[]) => setAccounts(Array.isArray(data) ? leafAccounts(data).filter(a => a.postingClass !== 'CASH') : []))
      .catch((e: any) => toast.showApiError(e, 'Failed to load accounts'));
    SegmentService.getByInstitution()
      .then(res => setSegments((res.content ?? []).filter((s: Segment) => s.active !== false)))
      .catch(() => setSegments([]));
    BankAccountService.listByInstitution(institutionId).then(setBanks).catch(() => setBanks([]));
    // ---- FW-4B: party pickers (active parties only: a rule never targets an inactive customer or supplier) ----
    Promise.resolve(CustomerService.listByInstitution(institutionId, 0, 200)).then(r => setCustomers(r?.content ?? [])).catch(() => setCustomers([]));
    Promise.resolve(SupplierService.listByInstitution(0, 200, undefined, institutionId)).then(r => setSuppliers(r?.content ?? [])).catch(() => setSuppliers([]));
    // ---- end FW-4B ----
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldOf = (code: string): RuleField | undefined => kind?.fields.find(f => f.code === code);
  const operatorsFor = (code: string): RuleOperator[] => {
    const f = fieldOf(code);
    return f && kind ? kind.operators[f.type] ?? [] : [];
  };

  const setCondition = (i: number, patch: Partial<ConditionRow>) =>
    setConditions(rows => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const changeField = (i: number, field: string) => {
    const ops = operatorsFor(field);
    setCondition(i, { field, operator: ops[0] ?? '', valueText: '', valueNumber: '', valueNumberTo: '' });
  };

  // a freshly loaded catalog gives rows without an operator their first valid one
  useEffect(() => {
    if (!kind) return;
    setConditions(rows => rows.map(r => {
      if (r.operator || !r.field) return r;
      const ops = operatorsFor(r.field);
      return { ...r, operator: ops[0] ?? '' };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const buildRequest = (): BankRuleRequest => ({
    name: name.trim(),
    scopeId: bankAccountId || null,
    matchMode,
    conditions: conditions.map(r => {
      const f = fieldOf(r.field);
      const op = r.operator as RuleOperator;
      return {
        field: r.field,
        operator: op,
        valueText: f?.type === 'NUMBER' ? null : r.valueText.trim(),
        valueNumber: f?.type === 'NUMBER' && r.valueNumber !== '' ? Number(r.valueNumber) : null,
        valueNumberTo: f?.type === 'NUMBER' && op === 'BETWEEN' && r.valueNumberTo !== '' ? Number(r.valueNumberTo) : null,
      };
    }),
    action: {
      actionType,
      accountId: af.account ? (accountId || null) : null,
      segmentId: af.segment ? (segmentId || null) : null,
      departmentId: af.department ? (departmentId || null) : null,
      customerId: af.customer ? (customerId || null) : null,
      supplierId: af.supplier ? (supplierId || null) : null,
      taxCodeId: af.taxSide ? (taxCodeId || null) : null,
      itemId: af.item ? (itemId || null) : null,
      descriptionTemplate: af.template ? (template.trim() || null) : null,
    },
  });

  const conditionsFilled = conditions.length > 0 && conditions.every(r => {
    const f = fieldOf(r.field);
    if (!f || !r.operator) return false;
    if (f.type === 'NUMBER') return r.valueNumber !== '' && (r.operator !== 'BETWEEN' || r.valueNumberTo !== '');
    return r.valueText.trim() !== '';
  });
  const actionFilled =
    (af.customer !== 'required' || !!customerId)
    && (!af.supplier || !!supplierId)
    && (af.account === 'post' || af.account === 'expense' ? !!accountId : true)
    && (!af.item || !!itemId || !!accountId);
  const valid = name.trim() !== '' && actionFilled && conditionsFilled;

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(await BankRuleService.test(institutionId, buildRequest()));
    } catch (e) {
      toast.showApiError(e, 'Failed to test the rule');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const saved = seed
        ? await BankRuleService.update(institutionId, seed.id, buildRequest())
        : await BankRuleService.create(institutionId, buildRequest());
      toast.showSuccess(seed ? 'Rule updated' : 'Rule created');
      onSaved(saved);
    } catch (e) {
      toast.showApiError(e, 'Failed to save the rule');
    } finally {
      setSaving(false);
    }
  };

  const maxConditions = kind?.maxConditions ?? 10;
  const accountOptions = useMemo(() => accounts, [accounts]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ 'data-help-page': 'bank.ruleEditor.page' } as any}>
      <DialogTitle>{seed ? 'Edit bank rule' : 'New bank rule'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 1 }} data-help-section="bank.ruleEditor.section.basics">
          <TextField data-help="bank.ruleEditor.field.name" label="Rule name" required size="small" value={name} onChange={e => setName(e.target.value)}
            inputProps={{ maxLength: 120 }} sx={{ flex: '2 1 260px', maxWidth: 420 }} />
          <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
            <SearchableSelect<BankAccount>
              data-help="bank.ruleEditor.field.bank" options={banks} value={bankAccountId} onChange={id => setBankAccountId(id)} size="small"
              getOptionLabel={b => `${b.bankName} — ${b.accountNumber}`} label="Bank account" placeholder="All bank accounts"
              data-testid="rule-bank-account" />
          </Box>
          <TextField data-help="bank.ruleEditor.field.matchMode" select label="Match" size="small" value={matchMode} onChange={e => setMatchMode(e.target.value as RuleMatchMode)}
            sx={{ flex: '1 1 220px', maxWidth: 300 }}>
            {(Object.keys(MATCH_MODE_LABEL) as RuleMatchMode[]).map(m => <MenuItem key={m} value={m}>{MATCH_MODE_LABEL[m]}</MenuItem>)}
          </TextField>
        </Box>

        <Typography variant="subtitle2">Conditions</Typography>
        {conditions.map((row, i) => {
          const f = fieldOf(row.field);
          const ops = operatorsFor(row.field);
          return (
            <Box key={i} data-help-section="bank.ruleEditor.section.conditions" data-testid={`rule-condition-${i}`} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <TextField data-help="bank.ruleEditor.field.condField" select size="small" label="Field" value={row.field} onChange={e => changeField(i, e.target.value)}
                sx={{ flex: '1 1 150px', maxWidth: 200 }}>
                {(kind?.fields ?? []).map(fl => <MenuItem key={fl.code} value={fl.code}>{fl.label}</MenuItem>)}
              </TextField>
              <TextField data-help="bank.ruleEditor.field.condOperator" select size="small" label="Operator" value={row.operator} onChange={e => setCondition(i, { operator: e.target.value as RuleOperator })}
                sx={{ flex: '1 1 160px', maxWidth: 210 }}>
                {ops.map(o => <MenuItem key={o} value={o}>{OPERATOR_LABEL[o]}</MenuItem>)}
              </TextField>
              {f?.type === 'ENUM' ? (
                <TextField data-help="bank.ruleEditor.field.condValue" select size="small" label="Value" value={row.valueText} onChange={e => setCondition(i, { valueText: e.target.value })}
                  sx={{ flex: '1 1 150px', maxWidth: 220 }}>
                  {f.enumOptions.map(o => <MenuItem key={o} value={o}>{o === 'IN' ? 'IN (deposit)' : o === 'OUT' ? 'OUT (withdrawal)' : o}</MenuItem>)}
                </TextField>
              ) : f?.type === 'NUMBER' ? (
                <>
                  <TextField data-help="bank.ruleEditor.field.condAmount" size="small" type="number" label={row.operator === 'BETWEEN' ? 'From' : 'Amount'} value={row.valueNumber}
                    onChange={e => setCondition(i, { valueNumber: e.target.value })} sx={{ flex: '1 1 140px', maxWidth: 200 }} />
                  {row.operator === 'BETWEEN' && (
                    <TextField data-help="bank.ruleEditor.field.condAmountTo" size="small" type="number" label="To" value={row.valueNumberTo}
                      onChange={e => setCondition(i, { valueNumberTo: e.target.value })} sx={{ flex: '1 1 140px', maxWidth: 200 }} />
                  )}
                </>
              ) : (
                <TextField data-help="bank.ruleEditor.field.condText" size="small" label="Text" value={row.valueText} onChange={e => setCondition(i, { valueText: e.target.value })}
                  inputProps={{ maxLength: 255 }} sx={{ flex: '2 1 200px', maxWidth: 360 }} />
              )}
              <IconButton data-help="bank.ruleEditor.removeCondition" size="small" aria-label="Remove condition" disabled={conditions.length <= 1}
                onClick={() => setConditions(rows => rows.filter((_, idx) => idx !== i))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
        <Box>
          <Button data-help="bank.ruleEditor.addCondition" size="small" startIcon={<AddIcon />} disabled={conditions.length >= maxConditions}
            onClick={() => setConditions(rows => [...rows, emptyCondition('DESCRIPTION')])}>Add condition</Button>
        </Box>

        <Typography variant="subtitle2">Then</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }} data-help-section="bank.ruleEditor.section.action">
          <TextField data-help="bank.ruleEditor.field.action" select size="small" label="Action" value={actionType} data-testid="rule-action-type"
            onChange={e => setActionType(e.target.value)} sx={{ flex: '1 1 220px', maxWidth: 320 }}
            helperText={ACTION_HELP[actionType]}>
            {(kind?.actions?.length ? kind.actions : ['POST_TO_ACCOUNT']).map(a =>
              <MenuItem key={a} value={a}>{ACTION_LABEL[a] ?? a}</MenuItem>)}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }} data-help-section="bank.ruleEditor.section.target">
          {af.customer && (
            <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
              <SearchableSelect<{ id: string; name: string }>
                data-help="bank.ruleEditor.field.customer" options={customers} value={customerId} onChange={id => setCustomerId(id)} size="small" clearable={af.customer === 'optional'}
                getOptionLabel={c => c.name} label={af.customer === 'optional' ? 'Customer' : 'Customer *'}
                placeholder={af.customer === 'optional' ? 'Walk-in customer' : 'Choose a customer'} data-testid="rule-customer" />
            </Box>
          )}
          {af.supplier && (
            <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
              <SearchableSelect<{ id: string; name: string }>
                data-help="bank.ruleEditor.field.supplier" options={suppliers} value={supplierId} onChange={id => setSupplierId(id)} size="small"
                getOptionLabel={c => c.name} label="Supplier *" placeholder="Choose a supplier" data-testid="rule-supplier" />
            </Box>
          )}
          {af.item && (
            <Box sx={{ flex: '1 1 240px', maxWidth: 360 }} data-help="bank.ruleEditor.field.item" data-testid="rule-item">
              <SalesCatalogueSelect value={itemId || null} kind="SERVICE" label="Service" onChange={id => setItemId(id)} />
            </Box>
          )}
          {af.account && (
            <Box sx={{ flex: '2 1 280px', maxWidth: 440 }}>
              <AccountAutocomplete data-help="bank.ruleEditor.field.account" accounts={accountOptions} value={accountId} onChange={setAccountId}
                label={af.account === 'post' ? 'Account' : af.account === 'expense' ? 'Expense account' : 'Revenue account (or pick an item)'}
                required={af.account !== 'revenue'} size="small" />
            </Box>
          )}
          {af.taxSide && (
            <Box sx={{ flex: '1 1 220px', maxWidth: 320 }} data-help="bank.ruleEditor.field.taxCode" data-testid="rule-tax-code">
              <TaxCodeSelect lookup={taxLookup} side={af.taxSide} value={taxCodeId || null} onChange={id => setTaxCodeId(id ?? '')} />
            </Box>
          )}
          {af.segment && (
            <Box sx={{ flex: '1 1 200px', maxWidth: 300 }}>
              <SearchableSelect<Segment>
                data-help="bank.ruleEditor.field.segment" options={segments} value={segmentId} onChange={id => setSegmentId(id)} size="small"
                getOptionLabel={s => `${s.code} - ${s.name}`} label="Segment" placeholder="(default)" data-testid="rule-segment" />
            </Box>
          )}
          {af.department && departmentOptions.length > 0 && (
            <Box sx={{ flex: '1 1 200px', maxWidth: 300 }} data-help="bank.ruleEditor.field.department">
              <DepartmentPicker options={departmentOptions} value={departmentId} onChange={setDepartmentId} label="Department" size="small" />
            </Box>
          )}
        </Box>
        {af.taxSide && (
          <Alert severity="info" sx={{ py: 0 }}>
            The bank amount includes the tax: it is split into net and tax with the code you choose, and the document is
            created through the normal bill / sale path (tax ledger and Mushak registers included). A rounding difference of
            up to 0.02 goes on a separate untaxed line. Nothing is e-mailed.
          </Alert>
        )}
        {af.template && (
          <TextField data-help="bank.ruleEditor.field.template" size="small" label="Journal description" value={template} onChange={e => setTemplate(e.target.value)}
            placeholder="{description}" inputProps={{ maxLength: 255 }}
            helperText="Tokens: {description}, {reference}, {date}. Blank uses the statement text."
            sx={{ flex: '1 1 320px', maxWidth: 640 }} />
        )}

        <Box>
          <Button data-help="bank.ruleEditor.test" variant="outlined" size="small" disabled={testing || !conditionsFilled || !actionFilled} onClick={handleTest}>
            Test against recent lines
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Looks at the last 90 days of statement lines. Nothing is posted.</Typography>
        </Box>
        {testResult && (
          <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="rule-test-result">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {testResult.count === 0 ? 'No recent statement line matches this rule.'
                : `${testResult.count} recent statement line${testResult.count === 1 ? '' : 's'} would match${testResult.truncated ? ' (first 1,000 lines searched)' : ''}.`}
            </Typography>
            {testResult.lines.slice(0, 12).map(l => (
              <Typography key={l.lineId} variant="caption" sx={{ display: 'block' }}>
                {formatDate(l.lineDate)} · {l.description || '—'} · {formatAmount(l.amount)}
              </Typography>
            ))}
            {testResult.lines.length > 12 && <Typography variant="caption" color="text.secondary">… and {testResult.count - 12} more</Typography>}
          </Paper>
        )}
        {!valid && <Alert severity="info" sx={{ py: 0 }}>Give the rule a name, at least one complete condition and the action's target.</Alert>}
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.ruleEditor.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.ruleEditor.save" variant="contained" disabled={saving || !valid} onClick={handleSave}>Save rule</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankRuleEditorDialog;
